import { hearsEveryThreadMessage, isThreadRowMuted } from '@common/helpers/channelNotifications'
import { IRecentConversationItem, IUnreadConversationSummary } from '@common/interfaces/channels'

/**
 * The single rule for whether a conversation's badge is a dot or a number,
 * shared by every sidebar row and the nav total so they can never disagree
 * about what a given unread state is worth.
 *
 * A number means "someone addressed this to you": a mention, a 1:1, or a group
 * chat you were put in. A dot means "there is something here" without a volume
 * worth reporting.
 *
 * The split is the conversation's shape, not its traffic. A conversation inside
 * a channel is the channel talking — everyone in the channel is a member of
 * every conversation in it (see CreateChannelConversation) — so counting it
 * would be reporting how busy a room has been. A group DM is the opposite: a
 * person put you in it, so every message in it is addressed to you.
 *
 * Arc is always a dot however much it has said, mentions included.
 *
 * `hasParticipated` is what promotes a group's thread out of that: once you
 * have posted in one it is a conversation you are part of, not a room you can
 * overhear, so it reports volume like anything else addressed to you. One you
 * have never posted in stays a dot, and a dot nobody drew you into does not
 * even move the nav badge. Being a Primary member of the group does the same for
 * every thread in it, and unmuting one thread under a muted group does it for that
 * thread — either way you asked to hear all of it.
 */
export interface IUnreadIndicatorInput {
  unreadCount: number
  mentionCount: number
  isArc: boolean
  /** A conversation inside a named channel, rather than a DM or an Arc chat. */
  isChannelConversation?: boolean
  /** The reader has posted in this conversation, so it is theirs to follow. */
  hasParticipated?: boolean
  /** The reader hears every message in this thread: Primary, or unmuted under a muted group. */
  hearsEveryMessage?: boolean
}

export type UnreadIndicator =
  | { kind: 'none' }
  /**
   * `countsTowardTotal` is false only for a channel conversation the reader has
   * not joined in on. Those are the one kind of unread that must not move the
   * nav badge's number — see `aggregateUnreadIndicator`.
   */
  | { kind: 'dot'; countsTowardTotal: boolean }
  | { kind: 'number'; count: number }

const DOT: UnreadIndicator = { kind: 'dot', countsTowardTotal: true }
const SILENT_DOT: UnreadIndicator = { kind: 'dot', countsTowardTotal: false }

export const getUnreadIndicator = ({
  unreadCount,
  mentionCount,
  isArc,
  isChannelConversation = false,
  hasParticipated = false,
  hearsEveryMessage = false,
}: IUnreadIndicatorInput): UnreadIndicator => {
  if (unreadCount <= 0 && mentionCount <= 0) return { kind: 'none' }
  if (isArc) return DOT

  // A conversation in a channel is the channel talking: everyone in the channel
  // is a member of every conversation in it, so its volume is not a number
  // anyone asked for. An @-mention is the exception — that is addressed to you.
  if (isChannelConversation) {
    if (mentionCount > 0) return { kind: 'number', count: mentionCount }
    // Having posted in it makes it yours to follow: from then on it is counted
    // like a conversation addressed to you, because in effect it is one.
    if (hasParticipated || hearsEveryMessage) return { kind: 'number', count: unreadCount }
    return SILENT_DOT
  }

  // Everything else is a person messaging you — a 1:1, or a group chat someone
  // put you in — so it is counted, by volume rather than by mentions: in a
  // conversation addressed to you the other messages are not background.
  return { kind: 'number', count: unreadCount }
}

/**
 * The indicator for a Threads row — shared by the row itself and the nav total.
 * A muted row has none: muting means not even a dot.
 */
export const getConversationUnreadIndicator = (
  conversation: IRecentConversationItem,
): UnreadIndicator =>
  isThreadRowMuted(conversation)
    ? { kind: 'none' }
    : getUnreadIndicator({
        unreadCount: conversation.unreadCount,
        mentionCount: conversation.mentionCount,
        isArc: conversation.isArc,
        isChannelConversation: !!conversation.parentChannelId,
        hasParticipated: conversation.hasParticipated,
        hearsEveryMessage: hearsEveryThreadMessage(
          conversation.groupSettings,
          conversation.overridesGroupMute ?? false,
        ),
      })

/**
 * Whether a conversation earns the reader's attention rather than just a dot:
 * an alert fires for it, and its unread moves the nav badge's number.
 *
 * The same answer for both because they are the same question. A channel
 * conversation nobody has drawn you into is something you can go and look at,
 * not something to be told about — which is how the threads it replaced worked.
 */
export const isDirectedAtReader = (indicator: UnreadIndicator): boolean =>
  indicator.kind === 'number' || (indicator.kind === 'dot' && indicator.countsTowardTotal)

/** How much an indicator contributes to a total that must equal "dots + numbers". */
export const unreadIndicatorValue = (indicator: UnreadIndicator): number => {
  switch (indicator.kind) {
    case 'none':
      return 0
    case 'dot':
      return indicator.countsTowardTotal ? 1 : 0
    case 'number':
      return indicator.count
  }
}

/**
 * The whole app's unread, as one indicator — what the Messages icon renders.
 *
 * Tri-state for the same reason a row is: a number the reader can act on, or a
 * dot saying "there is something, but counting it would tell you nothing".
 * Channel conversations you have not joined in on land in that second case, so
 * a busy channel cannot inflate the badge into noise; if they are all that is
 * unread, the icon still says so rather than going dark.
 */
export const aggregateUnreadIndicator = (
  indicators: readonly UnreadIndicator[],
): UnreadIndicator => {
  const count = indicators.reduce((total, indicator) => total + unreadIndicatorValue(indicator), 0)
  if (count > 0) return { kind: 'number', count }
  return indicators.some((indicator) => indicator.kind !== 'none') ? SILENT_DOT : { kind: 'none' }
}

/**
 * The nav badge across every conversation, not only the rows the Threads filter loaded.
 * A loaded row stands in for its summary, since sockets keep the row's counts live.
 */
export const aggregateConversationsUnread = (
  items: readonly IRecentConversationItem[],
  summaries: readonly IUnreadConversationSummary[],
): UnreadIndicator => {
  const loadedIds = new Set(items.map((item) => item.id))
  return aggregateUnreadIndicator([
    ...items.filter((item) => !item.isArchived).map(getConversationUnreadIndicator),
    ...summaries.filter((summary) => !loadedIds.has(summary.id)).map(getUnreadIndicator),
  ])
}
